import { Form } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { receiptApi } from '../../../../../apis'
import { AlertMessageEnum, getDateFormat } from '../../../../../common'
import {
  LoadingDataComponent,
  ManageModalComponent,
  ManageModalFooterComponent,
  NumberFormat,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { ReceiptEntity } from '../../../../../models'
import { RootState } from '../../../../../stores'
import { setAlert } from '../../../../../stores/actions'

type ManageReceiptModalComponentProps = {
  receiptId: number
}

const StyledLabel = styled.span`
  font-weight: bold;
`

export const ManageReceiptModalComponent: React.FC<ManageReceiptModalComponentProps> =
  ({ receiptId }) => {
    const dispatch = useDispatch()
    const { language } = useSelector((state: RootState) => state.language)
    const [loading, setLoading] = React.useState(false)
    const [receipt, setReceipt] = React.useState<ReceiptEntity>()

    React.useEffect(() => {
      const loadData = async () => {
        try {
          setLoading(true)
          const resp = await receiptApi.getReceiptById(receiptId)
          setReceipt(resp.data)
        } catch (e) {
          dispatch(
            setAlert({
              key: 'receipt.loadData.fail',
              type: AlertMessageEnum.ERROR,
            })
          )
        } finally {
          setLoading(false)
        }
      }
      loadData()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const body = (
      <Form>
        {loading && <LoadingDataComponent />}
        {!loading && receipt && (
          <>
            <Form.Item
              label={
                <StyledLabel>{translate('receipt.id', language)}</StyledLabel>
              }
            >
              {receipt.id}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.createdDate', language)}
                </StyledLabel>
              }
            >
              {getDateFormat(receipt.createdDate)}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.customer', language)}
                </StyledLabel>
              }
            >
              {receipt.card.customer?.name}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.customer.phone', language)}
                </StyledLabel>
              }
            >
              {receipt.card.customer?.phone}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.card.id', language)}
                </StyledLabel>
              }
            >
              {receipt.card.id}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.amount', language)}
                </StyledLabel>
              }
            >
              <NumberFormat
                value={receipt.amount}
                suffixKey="common.moneyType"
              />
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.reason', language)}
                </StyledLabel>
              }
            >
              {receipt.reason}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.transaction.id', language)}
                </StyledLabel>
              }
            >
              {receipt.transaction.id}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.transaction.time', language)}
                </StyledLabel>
              }
            >
              {getDateFormat(receipt.transaction.time, true)}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.station', language)}
                </StyledLabel>
              }
            >
              {`${receipt.transaction.pumpShift.pump.tank.station.name} (${receipt.transaction.pumpShift.pump.tank.station.address})`}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>{translate('receipt.fuel', language)}</StyledLabel>
              }
            >
              {receipt.transaction.pumpShift.pump.tank.fuel.name}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('receipt.creator', language)}
                </StyledLabel>
              }
            >
              {receipt.creator.name}
            </Form.Item>
          </>
        )}
        {!loading && <ManageModalFooterComponent />}
      </Form>
    )

    return <ManageModalComponent body={body} />
  }
